'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { login as apiLogin, getCurrentUser } from '@/lib/api'

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
}
const stagger = { visible: { transition: { staggerChildren: 0.1 } } }

export default function LoginPage() {
  const [mounted, setMounted] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const { login, user, isLoading } = useAuth()

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!isLoading && user) router.push('/dashboard')
  }, [user, isLoading, router])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await apiLogin(email, password)
      const userData = await getCurrentUser()
      login(userData)
      router.push('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!mounted || isLoading) return <div className="min-h-screen bg-ink" />

  return (
    <div className="min-h-screen bg-ink text-steel-100 flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 bg-grid animate-gridmove pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-electric-500/10 blur-[130px] pointer-events-none" />

      <header className="relative z-10 px-6 py-5">
        <Link href="/" className="inline-flex items-center gap-3">
          <div className="relative h-10 w-10 rounded-xl overflow-hidden ring-1 ring-electric-500/30 shadow-glow-sm">
            <Image src="/logo.png" alt="Crypto Credits" fill className="object-cover scale-[1.35]" />
          </div>
          <span className="font-display font-bold tracking-wide text-sm text-chrome">
            CRYPTO CREDITS
          </span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="w-full max-w-md">
          <motion.div variants={fadeIn} className="text-center mb-8">
            <h1 className="font-display font-bold text-3xl text-chrome mb-2">Welcome back</h1>
            <p className="text-steel-400">Sign in to manage your credit line</p>
          </motion.div>

          <motion.form variants={fadeIn} onSubmit={handleSubmit} className="glass rounded-3xl p-8 space-y-5">
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm text-steel-300 mb-2">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 px-4 bg-ink-soft border border-steel-700/60 rounded-xl text-white placeholder-steel-500 focus:border-electric-500 focus:outline-none transition-colors"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm text-steel-300 mb-2">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 bg-ink-soft border border-steel-700/60 rounded-xl text-white placeholder-steel-500 focus:border-electric-500 focus:outline-none transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-shimmer w-full h-12 rounded-xl font-semibold bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow hover:shadow-glow-lg transition-shadow disabled:opacity-60"
            >
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>
          </motion.form>

          <motion.p variants={fadeIn} className="text-center mt-6 text-sm text-steel-400">
            Don't have an account?{' '}
            <Link href="/register" className="text-electric-300 font-medium hover:underline">
              Create one
            </Link>
          </motion.p>
        </motion.div>
      </main>
    </div>
  )
}
